// Single source of truth for order-level status on the frontend.
// Adding a status = one new entry below; the type union, the dropdown
// order, the label map, and the color map all derive automatically.
const ORDER_STATUS_REGISTRY = [
  { value: 'NEW_ORDER',           label: 'New order',           color: 'bg-slate-100 text-slate-800' },
  { value: 'NEEDS_CONTENT',       label: 'Needs content',       color: 'bg-yellow-100 text-yellow-800' },
  { value: 'AWAITING_APPROVAL',   label: 'Awaiting approval',   color: 'bg-indigo-100 text-indigo-800' },
  { value: 'AWAITING_PRODUCTION', label: 'Awaiting production', color: 'bg-orange-100 text-orange-800' },
  { value: 'READY_TO_SHIP',       label: 'Ready to ship',       color: 'bg-teal-100 text-teal-800' },
  { value: 'SHIPPED',             label: 'Shipped',             color: 'bg-cyan-100 text-cyan-800' },
  { value: 'DELIVERED',           label: 'Delivered',           color: 'bg-green-100 text-green-800' },
  { value: 'COMPLETED',           label: 'Completed',           color: 'bg-gray-100 text-gray-800' },
  { value: 'EXCEPTION',           label: 'Needs attention',     color: 'bg-red-100 text-red-800' },
  { value: 'CANCELLED',           label: 'Cancelled',           color: 'bg-gray-100 text-gray-800' },
] as const;

export type OrderStatus = typeof ORDER_STATUS_REGISTRY[number]['value'];

export const ORDER_STATUSES: OrderStatus[] = ORDER_STATUS_REGISTRY.map(s => s.value);

// Item-level status for per-item workflow tracking
export type ItemStatus =
  | 'AWAITING_DETAILS'
  | 'ORIGIN_DRAFTING'
  | 'ORIGIN_APPROVED'
  | 'POEM_DRAFTING'
  | 'POEM_APPROVED'
  | 'FILES_READY_FOR_EDIT'
  | 'FILES_READY'
  | 'ENGRAVED'
  | 'CLEANED'
  | 'PRODUCED'
  | 'PACKAGED';

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

// Individual item within an order
export interface OrderItem {
  itemId: string;
  amazonOrderItemId?: string;
  name: string;
  pronouns: string;
  culturalContext?: string;
  notes?: string;
  poemThemes?: string;
  frameMaterial?: 'cherry' | 'sapele';
  needsProof?: boolean;
  status: ItemStatus;
  originDraft?: string;
  originApproved?: string;
  originConversation: ConversationMessage[];
  poemDraft?: string;
  poemApproved?: string;
  poemConversation: ConversationMessage[];
  pdfUrl?: string;
  svgUrl?: string;
  originHistory?: string[];
  poemHistory?: string[];
}

// Main order interface
export interface Order {
  orderId: string;
  amazonOrderId?: string;
  wooOrderId?: string;
  buyerName: string;
  source?: 'amazon' | 'manual' | 'web' | 'woocommerce';
  createdBy?: string;
  items: OrderItem[];
  status: OrderStatus;
  proofPdfUrl?: string;
  // Set by the WooCommerce cancel webhook when the order was already in
  // production at cancel time — surfaced in the list for manual review.
  cancellationRequestedAt?: number;
  // Stamped when the order enters AWAITING_APPROVAL; drives the 24h auto-advance.
  approvalRequestedAt?: number;
  // Stamped when delivery is confirmed (DELIVERED auto-advances to COMPLETED).
  deliveredAt?: number;
  channelId: string;
  threadTs: string;
  createdAt: number;
  updatedAt: number;
}

export const STATUS_LABELS: Record<OrderStatus, string> = Object.fromEntries(
  ORDER_STATUS_REGISTRY.map(s => [s.value, s.label])
) as Record<OrderStatus, string>;

export const STATUS_COLORS: Record<OrderStatus, string> = Object.fromEntries(
  ORDER_STATUS_REGISTRY.map(s => [s.value, s.color])
) as Record<OrderStatus, string>;

export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  AWAITING_DETAILS: 'Awaiting Details',
  ORIGIN_DRAFTING: 'Origin Drafting',
  ORIGIN_APPROVED: 'Origin Approved',
  POEM_DRAFTING: 'Poem Drafting',
  POEM_APPROVED: 'Poem Approved',
  FILES_READY_FOR_EDIT: 'Files Ready for Edit',
  FILES_READY: 'Files Ready',
  ENGRAVED: 'Engraved',
  CLEANED: 'Cleaned',
  PRODUCED: 'Produced',
  PACKAGED: 'Packaged',
};

export const ITEM_STATUS_COLORS: Record<ItemStatus, string> = {
  AWAITING_DETAILS: 'bg-yellow-100 text-yellow-800',
  ORIGIN_DRAFTING: 'bg-blue-100 text-blue-800',
  ORIGIN_APPROVED: 'bg-green-100 text-green-800',
  POEM_DRAFTING: 'bg-blue-100 text-blue-800',
  POEM_APPROVED: 'bg-green-100 text-green-800',
  FILES_READY_FOR_EDIT: 'bg-amber-100 text-amber-800',
  FILES_READY: 'bg-purple-100 text-purple-800',
  ENGRAVED: 'bg-emerald-100 text-emerald-800',
  CLEANED: 'bg-teal-100 text-teal-800',
  PRODUCED: 'bg-cyan-100 text-cyan-800',
  PACKAGED: 'bg-gray-200 text-gray-800',
};

